import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/modernization", label: "Modernization" },
  { to: "/servicenow", label: "ServiceNow" },
  { to: "/approach", label: "Our Approach" },
  { to: "/about", label: "About" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-slate-950/70 backdrop-blur-xl backdrop-saturate-150 border-amber-900/25"
          : "bg-slate-950/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="block w-2.5 h-2.5 bg-gold group-hover:rotate-45 transition-transform duration-500" />
          <span className="font-mono-tech text-sm tracking-[0.3em] text-slate-100">
            NEXUS<span className="text-gold">IPG</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="nav-links-desktop">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-mono-tech text-[13px] tracking-[0.2em] uppercase transition-colors duration-300 hover:text-amber-400 ${
                  isActive ? "text-amber-400" : "text-slate-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-assessment"
            className="group flex items-center gap-2 border border-amber-700/50 bg-amber-500/10 px-5 py-2.5 font-mono-tech text-xs tracking-[0.2em] uppercase text-amber-400 transition-[background-color,color,border-color] duration-300 hover:bg-amber-400 hover:text-ink hover:border-amber-400"
          >
            Assessment
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-amber-400 p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <nav
          data-testid="nav-links-mobile"
          className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-t border-amber-900/25 px-6 py-8 flex flex-col gap-6"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-mono-tech text-base tracking-[0.2em] uppercase ${isActive ? "text-amber-400" : "text-slate-200"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/contact"
            data-testid="nav-mobile-cta-assessment"
            className="font-mono-tech text-base tracking-[0.2em] uppercase text-amber-400"
          >
            Contact / Assessment
          </NavLink>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
